import type { ResearchConfigInput } from '@shared/config';
import { createDefaultConfig } from '@shared/config';
import { defaultAttributeSelection } from '@shared/attributes/catalog';
import type { DiscoverySourceId } from '@shared/sources';
import { useCallback, useMemo, useState } from 'react';

export const WIZARD_STEPS = [
  { id: 'sources', label: 'Sources', description: 'Where to look' },
  { id: 'setup', label: 'Source setup', description: 'Configure each source' },
  { id: 'search', label: 'Search', description: 'What and where' },
  { id: 'attributes', label: 'Attributes', description: 'What to collect' },
  { id: 'rules', label: 'Rules & enrichment', description: 'How to clean and filter' },
  { id: 'review', label: 'Review', description: 'Check before running' },
  { id: 'run', label: 'Run', description: 'Live progress' },
] as const;

export type WizardStepId = (typeof WIZARD_STEPS)[number]['id'];

/** Steps shown in the progress bar: everything up to and including Review. */
export const VISIBLE_STEP_COUNT = WIZARD_STEPS.length - 1;
/** Index of the Review step, the last one the Continue button walks to. */
export const REVIEW_STEP_INDEX = WIZARD_STEPS.findIndex((step) => step.id === 'review');

export interface WizardState {
  config: ResearchConfigInput;
  stepIndex: number;
  step: WizardStepId;
  setStep: (index: number) => void;
  next: () => void;
  back: () => void;
  update: (patch: Partial<ResearchConfigInput>) => void;
  setSources: (sources: DiscoverySourceId[]) => void;
  /** Validation messages for the current step, keyed by field. Empty means the step is complete. */
  stepErrors: Record<string, string>;
  canAdvance: boolean;
}

/** Fields required before each step is considered complete, mirroring ResearchConfigSchema. */
function validateStep(step: WizardStepId, config: ResearchConfigInput): Record<string, string> {
  const errors: Record<string, string> = {};
  const sources = config.sources ?? [];

  if (step === 'sources') {
    if (sources.length === 0) errors['sources'] = 'Select at least one source.';
  }

  // Each source that needs configuring blocks the step until it has what it needs, so a run can
  // never reach Review in a state the API would reject.
  if (step === 'setup') {
    if (sources.includes('linkedin')) {
      const linkedin = config.sourceOptions?.linkedin;
      if (!linkedin) errors['linkedin'] = 'Upload your LinkedIn export (Sales Navigator, Connections or Recruiter).';
      else if (!Object.values(linkedin.mapping).includes('name')) {
        errors['linkedin'] = 'Map one column to "Name" — a business needs a name to be imported.';
      }
    }
    if (sources.includes('any_website')) {
      const site = config.sourceOptions?.any_website;
      if (!site || site.startUrls.length === 0) errors['any_website'] = 'Add the page URL to extract from.';
      else if (!site.recipe && !site.recipeId) {
        errors['any_website'] = 'Analyse the page and confirm the extraction before continuing.';
      }
    }
  }

  if (step === 'search') {
    const needsKeywords = sources.some((source) => source === 'google_maps' || source === 'yellow_pages');
    const needsLocations = sources.some((source) => source === 'google_maps' || source === 'yellow_pages');
    if (needsKeywords && (config.search?.keywords?.length ?? 0) === 0) {
      errors['keywords'] = 'Add at least one keyword or business type.';
    }
    if (needsLocations && (config.search?.locations?.length ?? 0) === 0) {
      errors['locations'] = 'Add at least one location.';
    }
    if (!config.name?.trim()) errors['name'] = 'Give the research a name.';
  }

  if (step === 'attributes') {
    if ((config.attributes?.selected?.length ?? 0) === 0) errors['selected'] = 'Select at least one attribute.';
    for (const attribute of config.attributes?.custom ?? []) {
      if (!attribute.name.trim()) errors['custom'] = 'Every custom attribute needs a name.';
      else if (attribute.instruction.trim().length < 3) errors['custom'] = `Describe what "${attribute.name}" should find.`;
    }
  }

  if (step === 'rules') {
    const rules = config.rules;
    if (rules && rules.reviewThreshold !== undefined && rules.autoMergeThreshold !== undefined) {
      if (rules.reviewThreshold >= rules.autoMergeThreshold) {
        errors['reviewThreshold'] = 'The review threshold must be below the auto-merge threshold.';
      }
    }
  }

  return errors;
}

/**
 * Wizard draft state. The draft is a ResearchConfigInput throughout, so the review step submits
 * exactly what the API validates — there is no separate shadow shape to keep in sync.
 */
export function useWizard(initial?: ResearchConfigInput): WizardState {
  const [config, setConfig] = useState<ResearchConfigInput>(() => initial ?? createDefaultConfig());
  const [stepIndex, setStepIndex] = useState(0);

  const step = WIZARD_STEPS[Math.min(stepIndex, WIZARD_STEPS.length - 1)]?.id ?? 'sources';

  const update = useCallback((patch: Partial<ResearchConfigInput>) => {
    setConfig((current) => ({ ...current, ...patch }));
  }, []);

  /** Changing sources re-bases the attribute selection so it never references unavailable data. */
  const setSources = useCallback((sources: DiscoverySourceId[]) => {
    setConfig((current) => {
      const websiteEnrichment = current.enrichment?.website?.enabled ?? true;
      const available = new Set(defaultAttributeSelection({ sources, websiteEnrichment }));
      const kept = (current.attributes?.selected ?? []).filter((key) => key.startsWith('custom.') || available.has(key));
      const selected = kept.length > 0 ? kept : defaultAttributeSelection({ sources, websiteEnrichment });
      return {
        ...current,
        sources,
        attributes: { selected, custom: current.attributes?.custom ?? [] },
      };
    });
  }, []);

  const stepErrors = useMemo(() => validateStep(step, config), [step, config]);

  return {
    config,
    stepIndex,
    step,
    setStep: setStepIndex,
    next: () => setStepIndex((index) => Math.min(index + 1, WIZARD_STEPS.length - 1)),
    back: () => setStepIndex((index) => Math.max(index - 1, 0)),
    update,
    setSources,
    stepErrors,
    canAdvance: Object.keys(stepErrors).length === 0,
  };
}
